import styled, { css } from 'styled-components';
import { colors } from '~/styles/colors';

/**
 * Primitivas visuais da tela do Modo Eco simplificado, seguindo o design "DAM3 - Tela de Automação"
 * e os tokens do design system Celsius.
 */
const PRIMARY = colors.brand.primary.base;
const TEXT_STRONG = colors.text['main-900'];
const TEXT_SOFT = colors.text['sub-350'];
const TEXT_SUB = colors.text['sub-500'];
const STROKE = colors.stroke['soft-200'];
const MOBILE = '@media (max-width: 768px)';

export const mobileQuery = MOBILE;

export const Card = styled.section`
  min-width: 0;
  padding: 24px 28px;
  border: 1px solid ${STROKE};
  border-radius: 12px;
  background: ${colors.bg['white-0']};
  box-shadow: 0 1px 2px ${colors.celsius['shadow-soft']}, 0 6px 20px ${colors.celsius['shadow-card']};
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 6px 20px ${colors.celsius['shadow-hover']};
  }

  ${MOBILE} {
    padding: 18px 16px;
  }
`;

export const CardTitle = styled.h3`
  margin: 0;
  font-size: 19px;
  font-weight: 700;
  color: ${PRIMARY};
`;

export const FieldLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.35;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${TEXT_STRONG};
`;

export const HelperText = styled.p`
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 1.45;
  color: ${TEXT_SOFT};

  & b {
    color: ${TEXT_SUB};
  }
`;

export const ErrorText = styled.span`
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: ${colors.celsius['danger-base']};
`;

export const TwoColumns = styled.div<{ $left: string }>`
  display: grid;
  grid-template-columns: ${({ $left }) => $left} minmax(0, 1fr);
  gap: 32px;
  align-items: start;

  ${MOBILE} {
    grid-template-columns: minmax(0, 1fr);
    gap: 20px;
  }
`;

export const SidePanel = styled.div`
  min-width: 0;
  padding-left: 32px;
  border-left: 1px solid ${STROKE};

  ${MOBILE} {
    padding-top: 18px;
    padding-left: 0;
    border-top: 1px solid ${STROKE};
    border-left: 0;
  }
`;

export const FieldColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-width: 0;
`;

export const ToggleTrack = styled.button<{ $on: boolean }>`
  position: relative;
  flex: none;
  width: 44px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: ${({ $on }) => ($on ? PRIMARY : colors.stroke['sub-300'])};
  cursor: pointer;
  transition: background 0.2s;

  &::after {
    content: "";
    position: absolute;
    top: 2px;
    left: ${({ $on }) => ($on ? '22px' : '2px')};
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${colors.bg['white-0']};
    box-shadow: 0 1px 2px ${colors.celsius['shadow-knob']};
    transition: left 0.2s;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const CheckBox = styled.span<{ $checked: boolean }>`
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: ${({ $checked }) => ($checked ? 'none' : `1.6px solid ${colors.stroke['sub-300']}`)};
  border-radius: 6px;
  background: ${({ $checked }) => ($checked ? PRIMARY : colors.bg['white-0'])};
  transition: all 0.15s;

  & svg {
    opacity: ${({ $checked }) => ($checked ? 1 : 0)};
    transition: opacity 0.15s;
  }
`;

export const CheckRow = styled.label<{ $disabled?: boolean }>`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 11px;
  text-align: left;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
`;

/** Checkbox nativo, invisível: a caixa do design (`CheckBox`) vem logo depois e mostra o foco dele. */
export const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;

  &:focus-visible + span {
    outline: 2px solid ${PRIMARY};
    outline-offset: 2px;
  }
`;

export const SelectWrapper = styled.div`
  position: relative;

  & > svg {
    position: absolute;
    top: 50%;
    right: 14px;
    pointer-events: none;
    transform: translateY(-50%);
  }
`;

export const NativeSelect = styled.select<{ $bold?: boolean }>`
  width: 100%;
  height: 48px;
  padding: 0 40px 0 16px;
  border: 1px solid ${STROKE};
  border-radius: 8px;
  background: ${colors.bg['white-0']};
  font-family: inherit;
  font-size: 15px;
  font-weight: ${({ $bold }) => ($bold ? 600 : 400)};
  color: ${TEXT_STRONG};
  cursor: pointer;
  appearance: none;

  &:disabled {
    cursor: not-allowed;
    background: ${colors.LightGrey};
  }
`;

export const PrimaryButton = styled.button<{ $block?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  width: ${({ $block }) => ($block ? '100%' : 'auto')};
  padding: 13px 22px;
  border: none;
  border-radius: 8px;
  background: ${PRIMARY};
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${colors.text['white-0']};
  cursor: pointer;
  transition: background 0.15s;

  &:hover:not(:disabled) {
    background: ${colors.brand.principalHover};
    color: ${colors.White};
  }

  &:disabled {
    cursor: not-allowed;
    background: ${colors.stroke['sub-300']};
  }
`;

export const SecondaryButton = styled.button`
  padding: 12px 24px;
  border: 1px solid ${colors.stroke['sub-300']};
  border-radius: 8px;
  background: ${colors.bg['white-0']};
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  color: ${TEXT_SUB};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${colors.LightGrey};
    color: ${TEXT_STRONG};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const IconButton = styled.button<{ $danger?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: none;
  color: ${({ $danger }) => ($danger ? colors.celsius['danger-base'] : PRIMARY)};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${({ $danger }) => ($danger ? colors.celsius['danger-lighter'] : colors.brand.primary.lighter)};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  ${MOBILE} {
    width: 44px;
    height: 44px;
  }
`;

export const DayChip = styled.button<{ $on: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 34px;
  padding: 0 2px;
  border: none;
  border-radius: 8px;
  background: ${({ $on }) => ($on ? PRIMARY : colors.celsius['neutral-chip'])};
  white-space: nowrap;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: ${({ $on }) => ($on ? colors.text['white-0'] : TEXT_SOFT)};
  cursor: pointer;
  user-select: none;
  transition: all 0.15s;

  &:disabled {
    cursor: default;
  }

  ${MOBILE} {
    min-width: 28px;
    height: 46px;
    padding: 0;
    font-size: 11px;
    letter-spacing: 0;
  }
`;

export const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(32px, 1fr));
  gap: 6px;

  ${MOBILE} {
    grid-template-columns: repeat(7, minmax(28px, 1fr));
    gap: 4px;
  }
`;

export const DesktopOnly = styled.div`
  ${MOBILE} {
    display: none;
  }
`;

export const MobileOnly = styled.div`
  display: none;

  ${MOBILE} {
    display: block;
  }
`;

export const disabledLook = css`
  cursor: not-allowed;
  opacity: 0.6;
`;
